import React from "react";
import { PipsPuzzle, Condition } from "./constants";
import Domino from "./Domino";

interface GameBoardProps {
  puzzle: PipsPuzzle;
  onCellClick?: (row: number, col: number) => void;
  onDominoClick?: (dominoIndex: number, clickedRow: number, clickedCol: number, e: React.MouseEvent) => void;
  placedDominoes?: Array<{
    dominoIndex: number;
    cells: Array<{ row: number; col: number; pipValue: number }>;
  }>;
  dominoRotations?: Record<number, number>;
  selectedDominoIndex?: number | null;
}

// Helper to get condition display text
const getConditionText = (condition: Condition | null) => {
  if (!condition) return "";

  switch (condition.type) {
    case "sum":
      return condition.value?.toString() || "";
    case "equal":
      return "=";
    case "not-equal":
      return "≠";
    case "less-than":
      return `<${condition.value}`;
    case "greater-than":
      return `>${condition.value}`;
    default:
      return "";
  }
};

// Helper to get a more saturated border color based on the region color
const getSaturatedBorderColor = (color: string | undefined) => {
  if (!color) return "rgba(0,0,0,0.3)";

  // Map of region colors to their saturated border versions
  const borderColors: Record<string, string> = {
    "#e3f2fd": "#1976d2", // Light blue -> Saturated blue
    "#f3e5f5": "#7b1fa2", // Light purple -> Saturated purple
    "#fff9c4": "#f57f17", // Light yellow -> Saturated amber
    "#fce4ec": "#c2185b", // Light pink -> Saturated pink
    "#e8f5e9": "#388e3c", // Light green -> Saturated green
    "#fff3e0": "#e65100", // Light orange -> Saturated orange
    "#e1f5fe": "#0277bd", // Light cyan -> Saturated cyan
    "#e0f2f1": "#00695c", // Light teal -> Saturated teal
  };

  return borderColors[color] || "rgba(0,0,0,0.3)";
};

const GameBoard: React.FC<GameBoardProps> = ({
  puzzle,
  onCellClick,
  onDominoClick,
  placedDominoes = [],
  dominoRotations = {},
  selectedDominoIndex,
}) => {
  const { board, regions } = puzzle;

  // Calculate domino size based on grid dimensions and viewport
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const maxDimension = Math.max(board.width, board.height);
  const targetCellSize = isMobile ? 60 : 60; // Target cell size in pixels
  const maxContainerSize = isMobile ? Math.min(window.innerWidth - 32, 400) : maxDimension * targetCellSize;
  const cellSize = Math.floor(maxContainerSize / maxDimension);
  const dominoSize = Math.max(24, Math.min(cellSize * 0.8, 64)); // Between 24px and 64px

  // Track which cells we've already added condition labels to
  const labeledRegions = new Set<string>();

  // Build a map of occupied cells for quick lookup
  const cellData = new Map<string, { pipValue: number; dominoIndex: number }>();
  placedDominoes.forEach((placed) => {
    placed.cells.forEach((cell) => {
      cellData.set(`${cell.row},${cell.col}`, {
        pipValue: cell.pipValue,
        dominoIndex: placed.dominoIndex,
      });
    });
  });

  return (
    <div className="relative inline-block" style={{ width: maxContainerSize, maxWidth: maxContainerSize }}>
      {/* Background layer - the actual grid */}
      <div
        className="grid gap-1 w-full"
        style={{
          gridTemplateColumns: `repeat(${board.width}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${board.height}, minmax(0, 1fr))`,
          aspectRatio: `${board.width} / ${board.height}`,
        }}
      >
        {board.layout.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            // Check if this cell has a placed domino pip
            const data = cellData.get(`${rowIndex},${colIndex}`);

            // Skip empty string cells (but show debug info if there's a pip)
            if (cell === "") {
              return (
                <div key={`${rowIndex}-${colIndex}`} className="relative">
                  {data && (
                    <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-red-600 bg-red-100">
                      {data.pipValue}
                    </div>
                  )}
                </div>
              );
            }

            const region = regions[cell];
            const cellKey = `${rowIndex}-${colIndex}`;

            // Show label only on first cell of each region
            const shouldShowLabel = cell !== " " && !labeledRegions.has(cell) && region?.condition;

            if (shouldShowLabel) {
              labeledRegions.add(cell);
            }

            // Calculate border visibility (only for regions with borders)
            const showBorders = cell !== " " && region;
            const borders = showBorders
              ? {
                  top: rowIndex === 0 || board.layout[rowIndex - 1]?.[colIndex] !== cell,
                  bottom: rowIndex === board.height - 1 || board.layout[rowIndex + 1]?.[colIndex] !== cell,
                  left: colIndex === 0 || board.layout[rowIndex]?.[colIndex - 1] !== cell,
                  right: colIndex === board.width - 1 || board.layout[rowIndex]?.[colIndex + 1] !== cell,
                }
              : { top: false, bottom: false, left: false, right: false };

            const borderColor = getSaturatedBorderColor(region?.color);

            const isOccupied = data !== undefined;

            const handleCellClick = (e: React.MouseEvent) => {
              e.stopPropagation();
              // Only handle clicks on empty cells - domino overlays handle their own clicks
              if (!data) {
                onCellClick?.(rowIndex, colIndex);
              }
            };

            return (
              <div
                key={cellKey}
                onClick={handleCellClick}
                className={`relative w-full aspect-square ${!isOccupied ? "cursor-pointer hover:brightness-95 transition-all" : ""}`}
                style={{
                  backgroundColor: cell === " " ? "#e5e7eb" : region?.color || "#fff",
                  borderTop: borders.top ? `2px dashed ${borderColor}` : "none",
                  borderBottom: borders.bottom ? `2px dashed ${borderColor}` : "none",
                  borderLeft: borders.left ? `2px dashed ${borderColor}` : "none",
                  borderRight: borders.right ? `2px dashed ${borderColor}` : "none",
                }}
              >
                {/* Show condition label in the first cell of each region */}
                {shouldShowLabel && (
                  <div className="absolute -top-3 -left-3 z-20">
                    <div
                      className="rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                      style={{
                        backgroundColor: region?.color || "#fff",
                        border: `2px solid ${getSaturatedBorderColor(region?.color)}`,
                      }}
                    >
                      <span className="text-xs font-bold text-gray-800">{getConditionText(region.condition)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Render placed dominoes as overlays */}
        {placedDominoes.map((placed, idx) => {
          const firstCell = placed.cells[0];
          const domino = puzzle.dominoes[placed.dominoIndex];
          const rotation = dominoRotations[placed.dominoIndex] || 0;
          const normalizedRotation = rotation % 360;
          const isVertical = normalizedRotation === 90 || normalizedRotation === 270;

          const cellWidth = 100 / board.width;
          const cellHeight = 100 / board.height;

          return (
            <div
              key={placed.dominoIndex}
              className="absolute pointer-events-auto flex items-center justify-center"
              style={{
                left: `${firstCell.col * cellWidth}%`,
                top: `${firstCell.row * cellHeight}%`,
                width: isVertical ? `${cellWidth}%` : `${cellWidth * 2}%`,
                height: isVertical ? `${cellHeight * 2}%` : `${cellHeight}%`,
              }}
            >
              <Domino
                pips={domino}
                rotation={rotation}
                isSelected={selectedDominoIndex === placed.dominoIndex}
                onClick={(e) => onDominoClick?.(placed.dominoIndex, firstCell.row, firstCell.col, e)}
                size={dominoSize}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GameBoard;
